import React from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import DataTable from '../../components/ui/data-table';
import EmptyState from '../../components/ui/empty-state.jsx';
import LoadingSpinner from '../../components/common/LoadingSpinner.jsx';
import { useExams } from '../../hooks/useExams';
import { useNavigate } from 'react-router-dom';
import { Calendar, Clock, Users, Award, BarChart3, Eye, FileText } from 'lucide-react';

const StaffExams = () => {
  const navigate = useNavigate();
  const { exams, loading, error, fetchExams } = useExams({ scope: 'teacher' });

  const columns = [
    { id: 'title', header: 'Exam Title', accessorKey: 'title', sortable: true },
    { id: 'subject', header: 'Subject', accessorKey: 'subjectName', sortable: true },
    { id: 'class', header: 'Class', accessorKey: 'className' },
    { id: 'examDate', header: 'Date', accessorKey: 'examDate', sortable: true },
    { id: 'startTime', header: 'Start Time', accessorKey: 'startTime' },
    { id: 'duration', header: 'Duration', accessorKey: 'duration' },
    { id: 'status', header: 'Status', accessorKey: 'status', cell: ({ row }) => {
      const status = row.original.status;
      const statusColors = {
        draft: 'bg-gray-100 text-gray-800',
        scheduled: 'bg-blue-100 text-blue-800',
        active: 'bg-green-100 text-green-800',
        completed: 'bg-purple-100 text-purple-800',
        graded: 'bg-indigo-100 text-indigo-800'
      };
      return (
        <Badge className={statusColors[status] || 'bg-gray-100 text-gray-800'}>
          {status}
        </Badge>
      );
    }},
    {
      id: 'actions',
      header: 'Actions',
      cell: ({ row }) => (
        <div className="flex items-center gap-2">
          <Button 
            size="sm" 
            variant="outline" 
            onClick={() => navigate(`/dashboard/exams/${row.original.id}`)}
            title="View Exam Details"
          >
            <Eye className="w-4 h-4" />
          </Button>
        </div>
      )
    }
  ];

  const stats = {
    total: exams?.length || 0,
    upcoming: exams?.filter(e => e.status === 'scheduled' || e.status === 'active').length || 0,
    completed: exams?.filter(e => e.status === 'completed' || e.status === 'graded').length || 0,
    drafts: exams?.filter(e => e.status === 'draft').length || 0
  };

  const upcomingExams = exams?.filter(e => e.status === 'scheduled' || e.status === 'active') || [];
  const completedExams = exams?.filter(e => e.status === 'completed' || e.status === 'graded') || [];
  const draftExams = exams?.filter(e => e.status === 'draft') || [];

  if (loading) return <LoadingSpinner />;
  if (error) return (
    <div className="p-6">
      <Card>
        <CardContent className="p-6">
          <p className="text-red-600 text-sm">{String(error)}</p>
          <Button className="mt-3" onClick={fetchExams}>Retry</Button>
        </CardContent>
      </Card>
    </div>
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold">Exam Management</h2>
          <p className="text-gray-600">View and monitor exams (Read-only)</p>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Calendar className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Total Exams</p>
                <p className="text-2xl font-semibold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <Clock className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Upcoming</p>
                <p className="text-2xl font-semibold">{stats.upcoming}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <Award className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Completed</p>
                <p className="text-2xl font-semibold">{stats.completed}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-gray-100 rounded-lg">
                <FileText className="w-5 h-5 text-gray-600" />
              </div>
              <div>
                <p className="text-sm text-gray-600">Drafts</p>
                <p className="text-2xl font-semibold">{stats.drafts}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Exams Tabs */}
      <Tabs defaultValue="all" className="space-y-4">
        <TabsList>
          <TabsTrigger value="all">All Exams ({stats.total})</TabsTrigger>
          <TabsTrigger value="upcoming">Upcoming ({stats.upcoming})</TabsTrigger>
          <TabsTrigger value="completed">Completed ({stats.completed})</TabsTrigger>
          <TabsTrigger value="drafts">Drafts ({stats.drafts})</TabsTrigger>
        </TabsList>

        <TabsContent value="all">
          {(!exams || exams.length === 0) ? (
            <EmptyState 
              icon={Calendar}
              title="No exams yet"
              description="No exams have been created in the system."
            />
          ) : (
            <Card>
              <CardContent className="p-0">
                <DataTable data={exams} columns={columns} searchable />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="upcoming">
          {upcomingExams.length === 0 ? (
            <EmptyState 
              icon={Clock}
              title="No upcoming exams"
              description="No upcoming exams scheduled."
            />
          ) : (
            <Card>
              <CardContent className="p-0">
                <DataTable data={upcomingExams} columns={columns} searchable />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="completed">
          {completedExams.length === 0 ? (
            <EmptyState 
              icon={Award}
              title="No completed exams"
              description="No exams have been completed yet."
            />
          ) : (
            <Card>
              <CardContent className="p-0">
                <DataTable data={completedExams} columns={columns} searchable />
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="drafts">
          {draftExams.length === 0 ? (
            <EmptyState 
              icon={FileText}
              title="No draft exams"
              description="No draft exams available."
            />
          ) : (
            <Card>
              <CardContent className="p-0">
                <DataTable data={draftExams} columns={columns} searchable />
              </CardContent>
            </Card>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default StaffExams;




